import { useState, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { AppHeader } from '@/components/layout/AppHeader';
import { DateRangeFilter, DateRangeValue } from '@/components/policies/DateRangeFilter';
import { PolicyFilters, PolicySortOption } from '@/components/policies/PolicyFilters';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { useAuth } from '@/contexts/AuthContext';
import { useAuditData } from '@/hooks/useAuditData';
import { useCanAccessAudit } from '@/hooks/useCanAccessAudit';
import { useAckStats } from '@/hooks/useAckStats';
import { useClinic } from '@/hooks/useClinic';
import { usePolicies, usePolicyCategories } from '@/hooks/usePolicies';
import { exportAuditCsv } from '@/lib/exportCsv';
import { exportAuditPdf, exportPolicySummaryPdf } from '@/lib/exportPdf';
import { FileSpreadsheet, FileText, Download, Loader2, ShieldCheck, ListChecks, ArrowDownToLine, ChevronsUpDown, Check } from 'lucide-react';
import { BackArrow } from '@/components/BackArrow';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const DATE_RANGE_LABELS: Record<string, string> = {
  all_time: 'All Time',
  last_12_months: 'Last 12 months',
  last_18_months: 'Last 18 months',
  last_24_months: 'Last 24 months',
  last_3_years: 'Last 3 years',
  current_year: '2026',
  previous_year: '2025',
  '2024': '2024',
  '2023': '2023',
  custom: 'Custom range',
};

export default function AuditExport() {
  const { profile } = useAuth();
  const { canAccess, isLoading: accessLoading } = useCanAccessAudit();
  const { data: clinic } = useClinic();
  const { data: allPolicies } = usePolicies();
  const { data: ackStats } = useAckStats();
  const categories = usePolicyCategories(allPolicies);

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRangeValue>({
    preset: 'all_time',
    range: { from: null, to: null },
  });
  const [selectedSort, setSelectedSort] = useState<PolicySortOption>('next_review');
  const [exporting, setExporting] = useState<'csv' | 'pdf' | 'summary' | null>(null);

  // Individual Policy Packet state
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [generatingPacket, setGeneratingPacket] = useState(false);

  // Separate query for approved policies (combobox)
  const { data: approvedPoliciesForPacket } = useQuery({
    queryKey: ['approved-policies-for-packet', clinic?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('policies')
        .select('id, name, category')
        .eq('clinic_id', clinic!.id)
        .eq('status', 'Approved')
        .order('name', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!clinic?.id,
  });

  const { data: auditData, isLoading: auditLoading } = useAuditData({
    categories: selectedCategories.length > 0 ? selectedCategories : undefined,
    statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
  });

  // Apply date range filter and sorting on effective_date
  const filteredPolicies = useMemo(() => {
    if (!auditData?.policies) return [];
    const { from, to } = dateRange.range;
    let result = auditData.policies;
    if (from || to) {
      result = result.filter((p) => {
        const effective = new Date(p.effective_date);
        if (from && effective < from) return false;
        if (to && effective > to) return false;
        return true;
      });
    }
    // Apply sorting
    return [...result].sort((a, b) => {
      switch (selectedSort) {
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'category':
          return a.category.localeCompare(b.category) || a.name.localeCompare(b.name);
        case 'last_approved':
          return (b.last_review_date ?? '').localeCompare(a.last_review_date ?? '');
        case 'next_review':
        default:
          return a.next_review_date.localeCompare(b.next_review_date);
      }
    });
  }, [auditData?.policies, dateRange, selectedSort]);

  // Filter active policies for Policy Summary (not archived, Approved or Locked only)
  const activePolicies = useMemo(() => {
    return filteredPolicies
      .filter(p => p.status === 'Approved' || p.status === 'Locked')
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredPolicies]);

  const clinicName = clinic?.name ?? 'Clinic';

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedStatuses([]);
    setSelectedSort('next_review');
    setDateRange({ preset: 'all_time', range: { from: null, to: null } });
  };

  const handleExportSummary = async () => {
    if (activePolicies.length === 0) return;
    setExporting('summary');
    try {
      exportPolicySummaryPdf(activePolicies, clinicName, ackStats);
    } finally {
      setExporting(null);
    }
  };

  const handleExportCsv = async () => {
    if (!filteredPolicies.length || !profile) return;
    setExporting('csv');
    try {
      exportAuditCsv(
        filteredPolicies,
        auditData?.ownershipChanges ?? [],
        clinicName,
        profile.full_name
      );
    } finally {
      setExporting(null);
    }
  };

  const handleExportPdf = async () => {
    if (!filteredPolicies.length || !profile) return;
    setExporting('pdf');
    try {
      exportAuditPdf(
        filteredPolicies,
        auditData?.ownershipChanges ?? [],
        clinicName,
        profile.full_name,
        ackStats
      );
    } finally {
      setExporting(null);
    }
  };

  const handleSelectPolicy = async (policyId: string) => {
    const isSame = policyId === selectedPolicyId;
    setSelectedPolicyId(isSame ? null : policyId);
    setSelectedVersionId(null);
    setComboboxOpen(false);

    if (isSame) return;

    const { data, error } = await supabase
      .from('policy_versions')
      .select('id')
      .eq('policy_id', policyId)
      .eq('version_status', 'approved')
      .order('version_number', { ascending: false })
      .limit(1)
      .single();

    if (error || !data) {
      toast.error('Could not find an approved version for this policy.');
      setSelectedPolicyId(null);
      return;
    }
    setSelectedVersionId(data.id);
  };

  const handleDownloadPacket = async () => {
    if (!selectedVersionId || !selectedPolicyId) return;
    setGeneratingPacket(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-evidence-packet', {
        body: { policy_version_id: selectedVersionId },
      });
      if (error) throw error;

      const blob = new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const selectedPolicy = approvedPoliciesForPacket?.find(p => p.id === selectedPolicyId);
      const policyName = (selectedPolicy?.name ?? 'Policy').replace(/[^a-zA-Z0-9_-]/g, '_');
      const dateStr = new Date().toISOString().split('T')[0];
      const a = document.createElement('a');
      a.href = url;
      a.download = `${policyName}_EvidencePacket_${dateStr}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Evidence Packet generation failed. Please try again.');
    } finally {
      setGeneratingPacket(false);
    }
  };

  if (accessLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!canAccess) {
    return <Navigate to="/" replace />;
  }

  const totalPolicies = filteredPolicies.length;
  const totalVersions = filteredPolicies.reduce((sum, p) => sum + p.versions.length, 0);
  const totalAcks = filteredPolicies.reduce(
    (sum, p) => sum + p.versions.reduce((vSum, v) => vSum + v.acknowledgements.length, 0),
    0
  );

  const hasFilters = selectedCategories.length > 0 || selectedStatuses.length > 0 || dateRange.preset !== 'all_time';
  const filterSummary = hasFilters
    ? [selectedCategories.length > 0 ? `${selectedCategories.length} categories` : null, selectedStatuses.length > 0 ? `${selectedStatuses.length} statuses` : null, dateRange.preset !== 'all_time' ? DATE_RANGE_LABELS[dateRange.preset] : null].filter(Boolean).join(' / ')
    : 'All policies';

  return (
    <div className="min-h-screen bg-background pb-14 md:pb-0">
      <AppHeader />

      <main className="container px-4 py-8">
        <div className="mb-6 flex items-center">
          <BackArrow to="/" />
          <Download className="h-6 w-6 text-primary mr-3" />
          <h1 className="text-2xl md:text-[32px] font-extrabold tracking-tight">Audit Export</h1>
        </div>

        {/* Export Metadata */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Export Details</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-muted-foreground">Clinic</dt>
                <dd className="font-medium">{clinicName}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Generated By</dt>
                <dd className="font-medium">{profile?.full_name}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Export Scope</dt>
                <dd className="font-medium">{filterSummary}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Generated At</dt>
                <dd className="font-medium">{new Date().toLocaleString()}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <div className="space-y-8">
          {/* Two-column row: Policy Summary + Individual Policy Packet */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <section>
              <Card className="h-full">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <ListChecks className="h-8 w-8 text-primary" />
                    <div>
                      <CardTitle>Policy Summary</CardTitle>
                      <CardDescription className="mt-1.5">
                        Quick reference list of current active policies
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-xs text-muted-foreground">
                    Alphabetically sorted • Active policies only ({activePolicies.length} policies)
                  </p>
                  <Button
                    onClick={handleExportSummary}
                    disabled={auditLoading || exporting !== null || activePolicies.length === 0}
                    className="w-full sm:w-auto transition-all duration-fast ease-apple hover:brightness-[0.9] hover:scale-[1.02] active:brightness-[0.85] active:scale-[0.98] active:duration-100 cursor-pointer hover:text-white [&>svg]:hover:text-white"
                  >
                    {exporting === 'summary' ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    Download PDF
                  </Button>
                </CardContent>
              </Card>
            </section>

            <section>
              <Card className="h-full">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <FileText className="h-8 w-8 text-primary" />
                    <div>
                      <CardTitle>Individual Policy Packet</CardTitle>
                      <CardDescription className="mt-1.5">
                        Generate a complete Evidence Packet for any single approved policy.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-3">
                    <div className="w-full max-w-[480px]">
                      <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={comboboxOpen}
                            className="w-full justify-between font-normal"
                          >
                            {selectedPolicyId
                              ? approvedPoliciesForPacket?.find(p => p.id === selectedPolicyId)?.name ?? 'Select a policy...'
                              : 'Search or select a policy...'}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search policies..." />
                            <CommandList>
                              <CommandEmpty>No policies found.</CommandEmpty>
                              <CommandGroup>
                                {approvedPoliciesForPacket?.map((policy) => (
                                  <CommandItem
                                    key={policy.id}
                                    value={policy.name}
                                    onSelect={() => handleSelectPolicy(policy.id)}
                                  >
                                    <Check
                                      className={cn(
                                        'mr-2 h-4 w-4',
                                        selectedPolicyId === policy.id ? 'opacity-100' : 'opacity-0'
                                      )}
                                    />
                                    <span className="flex-1 truncate">{policy.name}</span>
                                    <Badge variant="secondary" className="ml-2 text-xs">
                                      {policy.category}
                                    </Badge>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    <Button
                      onClick={handleDownloadPacket}
                      disabled={!selectedVersionId || generatingPacket}
                      className="w-full md:w-auto transition-all duration-fast ease-apple hover:brightness-[0.9] hover:scale-[1.02] active:brightness-[0.85] active:scale-[0.98] active:duration-100 cursor-pointer hover:text-white [&>svg]:hover:text-white"
                    >
                      {generatingPacket ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <ArrowDownToLine className="mr-2 h-4 w-4" />
                      )}
                      {generatingPacket ? 'Generating...' : 'Download Evidence Packet'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </section>
          </div>

          <Separator />

          {/* Full Compliance Report */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-primary" />
              <div>
                <CardTitle>Full Compliance Report</CardTitle>
                <CardDescription className="mt-1.5">
                  Detailed audit package with versions &amp; acknowledgments
                </CardDescription>
              </div>
            </div>
            {/* Filters */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Filters</CardTitle>
                <CardDescription>
                  Filter the data before exporting. Exports will respect active filters.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2 items-center">
                <PolicyFilters
                  categories={categories}
                  selectedCategories={selectedCategories}
                  selectedStatuses={selectedStatuses}
                  selectedSort={selectedSort}
                  onCategoriesChange={setSelectedCategories}
                  onStatusesChange={setSelectedStatuses}
                  onSortChange={setSelectedSort}
                  onClearFilters={handleClearFilters}
                />
                <div className="shrink-0">
                  <DateRangeFilter value={dateRange} onChange={setDateRange} />
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            {auditLoading ? (
              <div className="grid gap-4 sm:grid-cols-3">
                <Skeleton className="h-24" />
                <Skeleton className="h-24" />
                <Skeleton className="h-24" />
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-3">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Policies</CardDescription>
                    <CardTitle className="text-3xl">{totalPolicies}</CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Versions</CardDescription>
                    <CardTitle className="text-3xl">{totalVersions}</CardTitle>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Acknowledgements</CardDescription>
                    <CardTitle className="text-3xl">{totalAcks}</CardTitle>
                  </CardHeader>
                </Card>
              </div>
            )}

            {/* Export Options */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="h-8 w-8 text-primary" />
                    <div>
                      <CardTitle>Export CSV</CardTitle>
                      <CardDescription>
                        Flattened compliance data for spreadsheet analysis
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={handleExportCsv}
                    disabled={auditLoading || exporting !== null}
                    className="w-full transition-all duration-fast ease-apple hover:brightness-[0.9] hover:scale-[1.02] active:brightness-[0.85] active:scale-[0.98] active:duration-100 cursor-pointer hover:text-white [&>svg]:hover:text-white"
                  >
                    {exporting === 'csv' ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    Download CSV
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <FileText className="h-8 w-8 text-destructive" />
                    <div>
                      <CardTitle>Export PDF</CardTitle>
                      <CardDescription>
                        Branded summary document for compliance records
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={handleExportPdf}
                    disabled={auditLoading || exporting !== null}
                    className="w-full transition-all duration-fast ease-apple hover:brightness-[0.9] hover:scale-[1.02] active:brightness-[0.85] active:scale-[0.98] active:duration-100 cursor-pointer hover:text-white [&>svg]:hover:text-white"
                  >
                    {exporting === 'pdf' ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    Download PDF
                  </Button>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Info */}
          <p className="text-sm text-muted-foreground">
            All exports are read-only and scoped to your clinic. Reports include policy details, 
            acknowledgements, and ownership history.
          </p>
        </div>
      </main>
    </div>
  );
}